import { stringToArrayBuffer } from '@conversed/shared/src/helpers/data.helpers';

import { getFileData } from '../helpers/fileHelper';

type TDownloadFileParams = {
  data: BlobPart;
  fileName: string;
  fileType: string;
};

type TDownloadFileTypeParam = Omit<TDownloadFileParams, 'fileType'>;

export const useFileDownload = () => {
  const download = ({ data, fileName, fileType }: TDownloadFileParams) => {
    // Create a blob with the data we want to download as a file
    const blob = new Blob([data], {
      type: fileType,
    });

    // Create an anchor element and dispatch a click event on it
    // to trigger a download
    const a = document.createElement('a');
    a.download = fileName;
    a.href = window.URL.createObjectURL(blob);

    const clickEvt = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    a.dispatchEvent(clickEvt);
    a.remove();
  };

  const downloadJSON = ({ data, fileName }: TDownloadFileTypeParam) => {
    download({ data, fileName, fileType: 'text/json' });
  };

  const downloadCSV = ({ data, fileName }: TDownloadFileTypeParam) => {
    download({ data, fileName, fileType: 'text/csv' });
  };

  const downloadHTML = ({ data, fileName }: TDownloadFileTypeParam) => {
    download({ data, fileName, fileType: 'text/html' });
  };

  const downloadXLS = ({ data, fileName }: TDownloadFileTypeParam) => {
    download({
      data,
      fileName,
      fileType:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,',
    });
  };

  const downloadFileByKey = (key: string, data: BlobPart) => {
    const { extension, fileName = '' } = getFileData(key);
    const parsedData = stringToArrayBuffer(window.atob(data.toString()));

    switch (extension) {
      case 'csv': {
        return downloadCSV({ data: parsedData, fileName });
      }
      default: {
        return downloadXLS({ data: parsedData, fileName });
      }
    }
  };

  /**
   * Downloads a file from base64-encoded buffer (example: SharePoint files)
   */
  const downloadFileFromBase64 = (
    fileBuffer: string,
    fileName: string,
    fileType: string,
  ) => {
    // Decode base64 to binary
    const binaryString = window.atob(fileBuffer);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    download({
      data: bytes,
      fileName,
      fileType,
    });
  };

  return {
    download,
    downloadJSON,
    downloadCSV,
    downloadXLS,
    downloadHTML,
    downloadFileByKey,
    downloadFileFromBase64,
  };
};
